"use client";

import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
} from "@/components/ui/card";

import { Button } from "../ui/button";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { getBlogPostPreviewHref } from "@/lib/blog-post-preview-link";
import { motion } from "framer-motion";

export type BlogPreviewPost = {
  node: {
    id: string;
    title: string;
    content?: string | null;
    date: string;
    slug?: string | null;
    author?: {
      node?: {
        name?: string | null;
        avatar?: {
          url?: string | null;
        } | null;
      } | null;
    } | null;
  };
  image?: {
    sourceUrl?: string | null;
    altText?: string | null;
  };
};

const BlogPreviewTile = ({
  post,
  excerpt,
  animationTrigger,
}: {
  post: BlogPreviewPost;
  excerpt: string | null;
  animationTrigger: boolean;
}) => {
  const postHref = post.node?.slug
    ? getBlogPostPreviewHref({
        slug: post.node.slug,
        title: post.node.title,
        image: post.image?.sourceUrl,
        authorName: post.node.author?.node?.name,
        authorAvatar: post.node.author?.node?.avatar?.url,
      })
    : "/blog";

  return (
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{
        y: animationTrigger ? 0 : 60,
        opacity: animationTrigger ? 1 : 0,
      }}
      transition={{ type: "spring", stiffness: 120, damping: 18, mass: 0.75 }}
      className="w-full"
    >
      <Link href={postHref} className="block group">
        <Card className="flex flex-col overflow-hidden rounded-lg border border-foreground/10 bg-card shadow-sm transition-shadow duration-300 group-hover:shadow-lg">
          <div className="w-full aspect-video overflow-hidden">
            <img
              src={post.image?.sourceUrl ?? ""}
              alt={post.image?.altText ?? "Post Image"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <CardContent className="p-6 flex flex-col gap-3">
            <CardTitle className="text-2xl md:text-3xl font-PlayfairDisplay font-semibold leading-snug group-hover:underline">
              {post.node?.title}
            </CardTitle>
            <p
              className="font-Lora text-base leading-relaxed text-foreground/75 dark:text-foreground/85 line-clamp-3"
              dangerouslySetInnerHTML={{
                __html: excerpt ?? "<p>No excerpt available.</p>",
              }}
            />
          </CardContent>
          <CardFooter className="px-6 pb-6 pt-0">
            <Button
              variant={"default"}
              className="w-full gap-2 font-Cabin"
            >
              Read on the blog
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </Link>
    </motion.div>
  );
};

export default BlogPreviewTile;
